import { useMemo, useState, useEffect } from "react";
import {
  Modal,
  Row,
  Col,
  Button,
  Tag,
  Typography,
  Space,
  Empty,
  Tooltip,
  Table,
} from "antd";
import { APIClient } from "../helpers/api_helper";
import { CloseOutlined } from "@ant-design/icons";

const { Title, Text } = Typography;
const api = new APIClient();

const MergeDrawer = ({
  open,
  title,
  leftTitle,
  rightTitle,
  items,
  tags, // fallback
  getId,
  renderItem,
  selectButtonLabel = "Izberi",
  mergeButtonLabel = "Združi",
  onCancel,
  onDone,
  mergeApiUrl,
  mergeInfoApiUrl, // NEW
}) => {
  const list = items && items.length ? items : tags || [];

  const getKey = useMemo(
    () => getId || ((it) => it?.id ?? it?.key ?? it?.uuid),
    [getId]
  );
  const fallbackRenderItem = (it) => (
    <Space size={6} wrap>
      <Tag color={it?.color || "default"}>
        {it?.title || it?.name || it?.label || String(getKey(it))}
      </Tag>
      {typeof it?.usageCount === "number" && (
        <Text type="secondary">({it.usageCount})</Text>
      )}
    </Space>
  );
  const renderRow = renderItem || fallbackRenderItem;

  const [targetId, setTargetId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mergeInfo, setMergeInfo] = useState(null); // NEW

  useEffect(() => {
    if (!open) {
      setTargetId(null);
      setLoading(false);
      setMergeInfo(null);
    }
  }, [open]);

  const targetItem = useMemo(
    () => list.find((it) => getKey(it) === targetId) || null,
    [list, targetId, getKey]
  );

  const sourceItems = useMemo(
    () => (targetItem ? list.filter((it) => getKey(it) !== targetId) : list),
    [list, targetItem, targetId, getKey]
  );

  const handleChooseTarget = (item) => {
    if (!loading) setTargetId(getKey(item));
  };
  const handleClearTarget = () => {
    if (!loading) {
      setTargetId(null);
      setMergeInfo(null);
    }
  };

  // 🔹 Fetch merge info when target changes
  useEffect(() => {
    if (!mergeInfoApiUrl || !targetItem) {
      setMergeInfo(null);
      return;
    }
    const payload = {
      sources: sourceItems.map((x) => x.id),
      target: targetItem.id,
    };
    api
      .get(mergeInfoApiUrl, payload)
      .then((resp) => setMergeInfo(resp))
      .catch(() => setMergeInfo(null));
  }, [targetItem, sourceItems, mergeInfoApiUrl]);

  const handleMerge = () => {
    if (!targetItem) return;
    const payload = {
      sources: sourceItems.map((x) => x.id),
      target: targetItem.id,
    };
    setLoading(true);
    api
      .post(mergeApiUrl, payload, { successMsg: "Združitev uspešna" })
      .then(() => {
        onDone();
      })
      .finally(() => setLoading(false));
  };

  const columns = [
    {
      title: "Združi iz",
      key: "item",
      render: (_, record) => renderRow(record),
    },
    {
      title: "",
      key: "action",
      width: 140,
      align: "right",
      render: (_, record) => (
        <Button
          size="small"
          disabled={loading}
          onClick={() => handleChooseTarget(record)}
        >
          {selectButtonLabel}
        </Button>
      ),
    },
  ];

  return (
    <Modal
      title={title}
      open={open}
      onCancel={loading ? undefined : onCancel}
      width={720}
      destroyOnClose
      footer={null}
      maskClosable={!loading}
    >
      <style>{`
        .merge-row-hover td { cursor: pointer; }
      `}</style>

      <Row gutter={[16, 16]}>
        {/* Left side */}
        <Col xs={24} md={12}>
          <Title level={5} style={{ marginBottom: 12 }}>
            {leftTitle}
          </Title>
          {sourceItems.length === 0 ? (
            <Empty description="Ni elementov" />
          ) : (
            <Table
              size="small"
              rowKey={(r) => String(getKey(r))}
              columns={columns}
              dataSource={sourceItems}
              pagination={false}
              rowClassName={() => "merge-row-hover"}
              onRow={(record) =>
                !loading ? { onClick: () => handleChooseTarget(record) } : {}
              }
            />
          )}
        </Col>

        {/* Right side */}
        <Col xs={24} md={12}>
          <Title level={5} style={{ marginBottom: 12 }}>
            {rightTitle}
          </Title>
          {!targetItem ? (
            <Empty
              description="Izberite cilj na levi."
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : (
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 8,
                }}
              >
                <Tag
                  color={targetItem?.color || "blue"}
                  style={{
                    fontSize: 18,
                    padding: "6px 16px",
                    borderRadius: 6,
                  }}
                >
                  {targetItem?.title || targetItem?.name}
                </Tag>
                <Tooltip title="Počisti cilj">
                  <Button
                    size="small"
                    type="text"
                    icon={<CloseOutlined />}
                    onClick={handleClearTarget}
                    disabled={loading}
                  />
                </Tooltip>
              </div>
              <Text
                type="secondary"
                style={{ fontSize: 12, display: "block", marginTop: 8 }}
              >
                {sourceItems.length} {sourceItems.length === 1 ? "element bo združen" : "elementov bo združenih"} v to.
              </Text>
            </div>
          )}
        </Col>
      </Row>

      {/* 🔹 Merge info row (below table, above footer) */}
      {mergeInfo && (
        <Row style={{ marginTop: 16 }}>
          <Col span={24}>
            <Text type="secondary">
              {mergeInfo.message ||
                `Ta združitev bo vplivala na ${mergeInfo.count} ${
                  mergeInfo.count === 1 ? "transakcijo" : "transakcij"
                }.`}
            </Text>
          </Col>
        </Row>
      )}

      {/* Footer */}
      <div
        style={{
          position: "sticky",
          bottom: 0,
          left: 0,
          right: 0,
          background: "#fff",
          padding: "12px 0",
          marginTop: 16,
          borderTop: "1px solid #f0f0f0",
          zIndex: 1,
        }}
      >
        <Row justify="end" gutter={8}>
          <Col>
            <Button
              type="primary"
              onClick={handleMerge}
              loading={loading}
              disabled={!targetItem || list.length < 2}
            >
              {mergeButtonLabel}
            </Button>
          </Col>
        </Row>
      </div>
    </Modal>
  );
};

export default MergeDrawer;
